import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const DataContext = createContext(null);

export const ADMIN_TABLE = "tbl0U96amynYFVuK";
export const USER_TABLE  = "tbl2Jyeqa8mRmRNg";

const WORKER_URL = "https://aquint-raebareli-task-management.aged-wildflower-4467.workers.dev";

async function apiFetch(path, options, token) {
  const res = await fetch(`${WORKER_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({ error: res.statusText }));
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

export function DataProvider({ children }) {
  const [adminRecords, setAdminRecords] = useState([]);
  const [userRecords,  setUserRecords]  = useState([]);
  const [larkUsers,    setLarkUsers]    = useState([]);
  // People deleted in Lark, with the date they went. Kept separate from
  // larkUsers so every existing consumer of larkUsers stays active-only.
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [commentCounts,setCommentCounts] = useState({});
  const [allPoints,    setAllPoints]    = useState({});
  const [commentLatestTimes, setCommentLatestTimes] = useState({});
  const [readComments, setReadComments] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tms_read_comments") || "{}"); } catch { return {}; }
  });
  // Per-task timestamp of when the user last marked its comments as read.
  // Used by the notification bell so a read notification stays visible for
  // 24 hours after being seen, then auto-disappears.
  const [commentSeenAt, setCommentSeenAt] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tms_comment_seen_at") || "{}"); } catch { return {}; }
  });
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [lastFetched,  setLastFetched]  = useState(null);

  const getToken = () => localStorage.getItem("tms_token");

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); 
    setError(null);
    try {
      const t = getToken();
      if (!t) return;

      const fetchList = [
        apiFetch(`/api/records/${ADMIN_TABLE}`, {}, t),
        apiFetch(`/api/records/${USER_TABLE}`,  {}, t),
        apiFetch(`/api/users`, {}, t),
        apiFetch(`/api/comments/counts`, {}, t).catch(() => ({ counts: {} })),
        apiFetch(`/api/points/all`, {}, t).catch(() => ({ allPoints: {} }))
      ];

      const [a, u, users, countsRes, pointsRes] = await Promise.all(fetchList);

      setAdminRecords(a.records || []);
      setUserRecords(u.records  || []);
      if (users) {
        setLarkUsers(users.users  || []);
        setDeletedUsers(users.deletedUsers || []);
      }
      if (countsRes && countsRes.counts) setCommentCounts(countsRes.counts);
      if (countsRes && countsRes.latestTimes) setCommentLatestTimes(countsRes.latestTimes);
      if (pointsRes && pointsRes.allPoints) setAllPoints(pointsRes.allPoints);
      
      setLastFetched(new Date().toLocaleTimeString());
    } catch (e) { setError(e.message); }
    finally     { if (!silent) setLoading(false); }
  }, []);

  const createTask = useCallback(async (fields) => {
    const data = await apiFetch(`/api/records/${ADMIN_TABLE}`, {
      method: "POST",
      body: JSON.stringify({ fields }),
    }, getToken());
    
    // Optimistic Update: Add new record to state immediately
    if (data.record) {
      setAdminRecords(prev => [data.record, ...prev]);
    }
    
    // Background refresh to sync associated user tasks
    refresh(true); 
    return data;
  }, [refresh]);

  const updateTask = useCallback(async (tableId, recordId, fields) => {
    const data = await apiFetch(`/api/records/${tableId}/${recordId}`, {
      method: "PATCH",
      body: JSON.stringify({ fields }),
    }, getToken());
    
    // Optimistic Update: Replace updated record in state
    if (data.record) {
      const updater = prev => prev.map(r => r.record_id === recordId ? data.record : r);
      if (tableId === ADMIN_TABLE) setAdminRecords(updater);
      else setUserRecords(updater);
    }
    
    // Background refresh for consistency
    refresh(true);
    return data;
  }, [refresh]);

  const deleteTask = useCallback(async (tableId, recordId) => {
    // Before deleting, find the task_id so we can clean up both tables in state
    let taskIdToClean = null;
    if (tableId === ADMIN_TABLE) {
      const adminRec = adminRecords.find(r => r.record_id === recordId);
      if (adminRec) {
        const tid = adminRec.fields?.["Task ID"];
        taskIdToClean = typeof tid === "string" ? tid : (tid?.[0]?.text || tid?.[0]?.value || null);
      }
    }

    const data = await apiFetch(`/api/records/${tableId}/${recordId}`, {
      method: "DELETE",
    }, getToken());

    // Optimistic Update: Remove from both admin and user state
    if (tableId === ADMIN_TABLE) {
      setAdminRecords(prev => prev.filter(r => r.record_id !== recordId));
      // Also remove associated user records by task_id
      if (taskIdToClean) {
        setUserRecords(prev => prev.filter(r => {
          const utid = r.fields?.["Task ID"];
          const utidStr = typeof utid === "string" ? utid : (utid?.[0]?.text || utid?.[0]?.value || "");
          return utidStr !== taskIdToClean;
        }));
      }
    } else {
      setUserRecords(prev => prev.filter(r => r.record_id !== recordId));
    }

    // Background refresh
    refresh(true);
    return data;
  }, [refresh, adminRecords]);

  // Bulk: strip one or more people off many tasks in a single request.
  // The per-task loop this replaces took minutes and overloaded the worker.
  const bulkRemoveAssignee = useCallback(async (recordIds, removeUserIds) => {
    return apiFetch(`/api/bulk/remove-assignee`, {
      method: "POST",
      body: JSON.stringify({ recordIds, removeUserIds }),
    }, getToken());
  }, []);

  const runCron = useCallback(async () => {
    return apiFetch("/api/cron/run", { method: "POST" }, getToken());
  }, []);

  const getAttachments = useCallback(async (taskId) => {
    const data = await apiFetch(`/api/attachments/${taskId}`, {}, getToken());
    return data.attachments || [];
  }, []);

  const deleteAttachment = useCallback(async (taskId, fileToken) => {
    return apiFetch(`/api/attachments/${taskId}/${fileToken}`, {
      method: "DELETE",
    }, getToken());
  }, []);

  const reopenUserTask = useCallback(async (taskId, userId) => {
    return apiFetch(`/api/supervisor/reopen`, {
      method: "POST",
      body: JSON.stringify({ taskId, userId }),
    }, getToken());
  }, []);

  const getComments = useCallback(async (taskId) => {
    const data = await apiFetch(`/api/comments/${taskId}`, {}, getToken());
    return data.comments || [];
  }, []);

  const addComment = useCallback(async (taskId, text) => {
    return apiFetch(`/api/comments/${taskId}`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }, getToken());
  }, []);

  const getPoints = useCallback(async (taskId) => {
    const data = await apiFetch(`/api/points/${taskId}`, {}, getToken());
    return data.points || [];
  }, []);

  const addPoint = useCallback(async (taskId, text) => {
    const data = await apiFetch(`/api/points/${taskId}`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }, getToken());
    return data;
  }, []);

  const updatePoint = useCallback(async (taskId, pointId, text) => {
    const data = await apiFetch(`/api/points/${taskId}/${pointId}`, {
      method: "PATCH",
      body: JSON.stringify({ text }),
    }, getToken());
    return data;
  }, []);

  const deletePoint = useCallback(async (taskId, pointId) => {
    const data = await apiFetch(`/api/points/${taskId}/${pointId}`, {
      method: "DELETE",
    }, getToken());
    return data;
  }, []);

  const uploadFile = useCallback(async (file, taskId) => {
    const token = getToken();
    const formData = new FormData();
    formData.append("file", file);
    formData.append("taskId", taskId || "");
    const res = await fetch(`${WORKER_URL}/api/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data;
  }, []);

  const refreshUsers = useCallback(async () => {
    try {
      const t = getToken();
      const users = await apiFetch(`/api/users?refresh=true`, {}, t);
      if (users) {
        setLarkUsers(users.users || []);
        setDeletedUsers(users.deletedUsers || []);
      }
    } catch (e) { console.error("User refresh failed:", e.message); }
  }, []);

  const markCommentsRead = useCallback((taskId, count) => {
    setReadComments(prev => {
      if (prev[taskId] === count) return prev;
      const next = { ...prev, [taskId]: count };
      localStorage.setItem("tms_read_comments", JSON.stringify(next));
      return next;
    });
    // Stamp the "seen" time so the notification bell can hide it after 24h.
    setCommentSeenAt(prev => {
      const next = { ...prev, [taskId]: Date.now() };
      localStorage.setItem("tms_comment_seen_at", JSON.stringify(next));
      return next;
    });
  }, []);

  // ── Live comment-counts polling (Option 1) ────────────────────
  // Fetches /api/comments/counts every 20s while the tab is visible,
  // so the NotificationBell badge / dropdown update without a page
  // refresh when a new comment arrives. Only touches comment-related
  // state — no task data, no other concepts changed.
  useEffect(() => {
    let intervalId = null;

    const pollOnce = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      const t = getToken();
      if (!t) return;
      try {
        const res = await apiFetch(`/api/comments/counts`, {}, t);
        if (res?.counts) setCommentCounts(res.counts);
        if (res?.latestTimes) setCommentLatestTimes(res.latestTimes);
      } catch (e) {
        // Network blip — next tick will recover.
      }
    };

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(pollOnce, 20000); // 20 s cadence
    };
    const stop = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    const onVis = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) { stop(); }
      else { pollOnce(); start(); }
    };

    start();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVis);
    }
    return () => {
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVis);
      }
    };
  }, []);

  return (
    <DataContext.Provider value={{
      adminRecords, userRecords, larkUsers, deletedUsers, bulkRemoveAssignee, commentCounts, commentLatestTimes, readComments, commentSeenAt, allPoints, setAllPoints,
      loading, error, lastFetched,
      refresh, createTask, updateTask, deleteTask, runCron, getComments, addComment, getPoints, addPoint, updatePoint, deletePoint, uploadFile, getAttachments, deleteAttachment, reopenUserTask, refreshUsers, markCommentsRead,
      ADMIN_TABLE, USER_TABLE,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
